import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';
import type {
  TableColumn,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { SearchFormComponent } from '@shared/components/search-form/search-form.component';
import {
  SYNC_REPORT_TABLE_COLUMNS,
  SYNC_REPORT_SEARCH_CONFIG,
  SyncErrorReportSearchParams,
  SyncLogItem,
} from './sync-report.config';
import { SyncReportService } from './services/sync-report.service';

@Component({
  selector: 'app-sync-report',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    TranslateModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
  ],
  providers: [MessageService],
  templateUrl: './sync-report.component.html',
  styleUrls: ['./sync-report.component.css'],
})
export class SyncReportComponent implements OnInit {
  searchConfig = SYNC_REPORT_SEARCH_CONFIG;
  columns: TableColumn[] = SYNC_REPORT_TABLE_COLUMNS;

  syncLogs = signal<SyncLogItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  currentPage = signal(1);
  pageSize = signal(20);

  private currentFilters: SyncErrorReportSearchParams = {};

  private readonly permissionService = inject(PermissionService);
  private readonly syncReportService = inject(SyncReportService);
  private readonly messageService = inject(MessageService);

  canRead = computed(() =>
    this.permissionService.canDoAction('product.sync_report.view'),
  );

  ngOnInit(): void {
    this.loadReport();
  }

  onSearch(searchCriteria: SyncErrorReportSearchParams): void {
    this.currentFilters = {
      eventType: searchCriteria.eventType || undefined,
      schemaVersion: searchCriteria.schemaVersion || undefined,
      result: searchCriteria.result || undefined,
      entityType: searchCriteria.entityType || undefined,
      dateFrom: searchCriteria.dateFrom
        ? new Date(searchCriteria.dateFrom).toISOString()
        : undefined,
      dateTo: searchCriteria.dateTo
        ? new Date(searchCriteria.dateTo).toISOString()
        : undefined,
    };
    this.currentPage.set(1);
    this.loadReport();
  }

  onReset(): void {
    this.currentFilters = {};
    this.currentPage.set(1);
    this.loadReport();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.loadReport();
  }

  formatDateTime = formatDateTime;

  eventTypeI18nKey(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const snake = value.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
    return `sync_report.event_types.${snake}`;
  }

  resultI18nKey(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return `sync_report.results.${value.toLowerCase()}`;
  }

  operationI18nKey(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return `sync_report.operations.${value.toLowerCase()}`;
  }

  private loadReport(): void {
    this.loading.set(true);

    const params: SyncErrorReportSearchParams = {
      ...this.currentFilters,
      page: this.currentPage() - 1,
      size: this.pageSize(),
    };

    this.syncReportService.getSyncErrorReport(params).subscribe({
      next: (response) => {
        this.syncLogs.set(response.data);
        this.totalRecords.set(response.totalRecords);
        this.loading.set(false);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'common.actions.error',
          detail: 'sync_report.messages.error_loading_report',
        });
        this.loading.set(false);
      },
    });
  }
}
